import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  TrendingUp, BarChart3, Target, Brain, HelpCircle, Settings, 
  LogOut, Shield, ChevronLeft, ChevronRight, GraduationCap
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const Sidebar = ({ user, onLogout }) => {
  const { t, language } = useLanguage();
  const location = useLocation();
  
  // Get collapsed state from localStorage or default to true (collapsed)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const navItems = [
    { 
      icon: BarChart3, 
      label: language === 'de' ? 'Dashboard' : 'Dashboard', 
      path: '/dashboard' 
    },
    { 
      icon: Target, 
      label: language === 'de' ? 'Strategien' : 'Strategies', 
      path: '/strategies' 
    },
    { 
      icon: Brain, 
      label: 'KI Mentor', 
      path: '/mentor' 
    },
    { 
      icon: GraduationCap, 
      label: language === 'de' ? 'Wissensdatenbank' : 'Knowledge Base', 
      path: '/wissensdatenbank' 
    },
    { 
      icon: HelpCircle, 
      label: 'Support', 
      path: '/support' 
    },
    ...(user?.is_admin ? [{
      icon: Shield,
      label: 'Admin',
      path: '/admin/dashboard'
    }] : []),
    { 
      icon: Settings, 
      label: language === 'de' ? 'Einstellungen' : 'Settings', 
      path: '/settings' 
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <TooltipProvider delayDuration={0}>
      <aside className={`${isCollapsed ? 'w-16' : 'w-56'} transition-all duration-300 border-r border-white/[0.06] bg-black/40 flex flex-col h-screen sticky top-0`}>
        {/* Header with Logo */}
        <div className="p-3 border-b border-white/[0.06] flex items-center gap-2 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 overflow-hidden flex-1">
            <div className="w-10 h-10 min-w-[40px] rounded-xl bg-primary flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            {!isCollapsed && (
              <span className="font-semibold text-sm whitespace-nowrap">TradeJourn.ai</span>
            )}
          </Link>
        </div>
        
        {/* Toggle Button - positioned at edge */}
        <button
          className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-zinc-900 border border-white/[0.1] flex items-center justify-center text-zinc-500 hover:text-white hover:border-white/[0.2] transition-all z-10"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>
        
        {/* Navigation - scrollable area */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const content = (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-primary/15 text-primary' 
                    : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04]'
                }`}
              >
                <item.icon className="h-5 w-5 min-w-[20px]" />
                {!isCollapsed && (
                  <span className="text-sm whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );

            // Wrap in tooltip only when collapsed
            if (isCollapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    {content}
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return <div key={item.path}>{content}</div>;
          })}
        </nav>
        
        {/* User Section - Fixed at bottom */}
        <div className="flex-shrink-0 p-3 border-t border-white/[0.06]">
          {!isCollapsed ? (
            <>
              <div className="flex items-center gap-3 mb-3 px-1">
                <div className="w-8 h-8 min-w-[32px] rounded-full bg-white/[0.08] flex items-center justify-center">
                  <span className="text-sm font-medium text-zinc-300">{user?.email?.[0]?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-zinc-300">{user?.email}</p>
                  <p className="text-xs text-zinc-500 capitalize">
                    {user?.subscription_plan || 'Basic'}
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04] px-3"
                onClick={onLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Abmelden' : 'Logout'}
              </Button>
            </>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={onLogout}
                  className="w-full p-2.5 rounded-xl text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04] transition-all flex justify-center"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {language === 'de' ? 'Abmelden' : 'Logout'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
